import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import DeveloperPortalLayout from './DeveloperPortalLayout';
import Dashboard from './Dashboard';
import Venues from './Venues';
import DataExplorer from './DataExplorer';
import Charts from './Charts';
import Applications from './Applications';
import VenueMappings from './VenueMappings';
import RegisterOAuthApp from './RegisterOAuthApp';
import Documentation from './Documentation';
import Settings from './Settings';
import SectionHeader from '../../components/SectionHeader/SectionHeader';
import PlatefullAILogo from '../../images/PlatefullAILogoNB.svg';
import './DeveloperPortal.css';
import onboardStyles from '../../screens/Onboard/Onboard.module.css';

type Screen = 'dashboard' | 'venues' | 'data-explorer' | 'charts' | 'applications' | 'venue-mappings' | 'register' | 'test' | 'docs' | 'settings';

const DeveloperPortal: React.FC = () => {
  const { user, login, logout, register } = useAuth();
  const [currentScreen, setCurrentScreen] = useState<Screen>('dashboard');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showRegisterForm, setShowRegisterForm] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    const email = (document.getElementById('login-email') as HTMLInputElement)?.value;
    const password = (document.getElementById('login-password') as HTMLInputElement)?.value;

    if (!email || !password) {
      setError('Email and password are required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await login(email, password);
    } catch (err) {
      setError('Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    const email = (document.getElementById('register-email') as HTMLInputElement)?.value;
    const firstName = (document.getElementById('register-firstName') as HTMLInputElement)?.value;
    const lastName = (document.getElementById('register-lastName') as HTMLInputElement)?.value;
    const password = (document.getElementById('register-password') as HTMLInputElement)?.value;
    const confirmPassword = (document.getElementById('register-confirm-password') as HTMLInputElement)?.value;

    if (!email || !firstName || !lastName || !password || !confirmPassword) {
      setError('All fields are required');
      setLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      setLoading(false);
      return;
    }

    // Additional password validation
    if (!/(?=.*[a-z])/.test(password)) {
      setError('Password must contain at least one lowercase letter');
      setLoading(false);
      return;
    }
    if (!/(?=.*[A-Z])/.test(password)) {
      setError('Password must contain at least one uppercase letter');
      setLoading(false);
      return;
    }
    if (!/(?=.*\d)/.test(password)) {
      setError('Password must contain at least one number');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await register(email, password, firstName, lastName);
      setError(null);
      setLoading(false);
      setShowRegisterForm(false);
    } catch (err) {
      setError('Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const LoginForm = () => (
    <div className={onboardStyles.onboardContainer}>
      <div className={onboardStyles.onboardContent}>
        <div className={onboardStyles.stepContainer}>
          <div className={onboardStyles.stepHeader}>
            <div className={onboardStyles.logoContainer}>
              <img src={PlatefullAILogo} alt="PLATEFULL AI Logo" className={onboardStyles.logoImage} />
            </div>
            <h2 className={onboardStyles.stepTitle}>Business Portal</h2>
          </div>
          
          <div className={onboardStyles.stepDescription}>
            <div className={onboardStyles.stepDescriptionText}>
              <p>Sign in to manage your OAuth applications</p>
            </div>
          </div>

          <div className={onboardStyles.stepContent}>
            <form onSubmit={handleLogin} autoComplete="off" style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              {/* Hidden dummy fields to confuse password managers */}
              <input type="text" style={{ display: 'none' }} />
              <input type="password" style={{ display: 'none' }} />
              
              <div className={onboardStyles.formGroup}>
                <label htmlFor="login-email" className={onboardStyles.formLabel}>
                  Email
                </label>
                <input
                  id="login-email"
                  type="email"
                  required
                  placeholder="business@example.com"
                  className={onboardStyles.formInput}
                  autoComplete="username"
                  autoFocus
                  disabled={loading}
                />
              </div>
              
              <div className={onboardStyles.formGroup}>
                <label htmlFor="login-password" className={onboardStyles.formLabel}>
                  Password
                </label>
                <input
                  id="login-password"
                  type="password"
                  required
                  placeholder="Your password"
                  className={onboardStyles.formInput}
                  autoComplete="current-password"
                  disabled={loading}
                />
              </div>

              {error && (
                <div className={onboardStyles.errorMessage}>
                  {error}
                </div>
              )}
            </form>
          </div>

          <div className={onboardStyles.linkContainer}>
            <button
              type="button"
              onClick={() => setShowRegisterForm(true)}
              className={onboardStyles.toggleLink}
              disabled={loading}
            >
              Don't have an account?
            </button>
          </div>

          <div className={onboardStyles.buttonContainer}>
            <button
              type="button"
              onClick={handleLogin}
              disabled={loading}
              className={onboardStyles.primaryButton}
            >
              <span>
                {loading && <span className={onboardStyles.loadingSpinner}></span>}
                {loading ? 'Signing in...' : 'Sign In'}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const RegisterForm = () => (
    <div className={onboardStyles.onboardContainer}>
      <div className={onboardStyles.onboardContent}>
        <div className={onboardStyles.stepContainer}>
          <div className={onboardStyles.stepHeader}>
            <div className={onboardStyles.logoContainer}>
              <img src={PlatefullAILogo} alt="PLATEFULL AI Logo" className={onboardStyles.logoImage} />
            </div>
            <h2 className={onboardStyles.stepTitle}>Create Business Account</h2>
          </div>
          
          <div className={onboardStyles.stepDescription}>
            <div className={onboardStyles.stepDescriptionText}>
              <p>Register to start building with PlatefullAI OAuth</p>
            </div>
          </div>

          <div className={onboardStyles.stepContent}>
            <form onSubmit={handleRegister} autoComplete="off" style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              {/* Hidden dummy fields to confuse password managers */}
              <input type="text" style={{ display: 'none' }} />
              <input type="password" style={{ display: 'none' }} />
              
              <div className={onboardStyles.formGroup}>
                <label htmlFor="register-email" className={onboardStyles.formLabel}>
                  Email
                </label>
                <input
                  id="register-email"
                  type="email"
                  required
                  placeholder="business@example.com"
                  className={onboardStyles.formInput}
                  autoComplete="username"
                  autoFocus
                  disabled={loading}
                />
              </div>

              <div className={onboardStyles.formRow}>
                <div className={onboardStyles.formGroup}>
                  <label htmlFor="register-firstName" className={onboardStyles.formLabel}>
                    First Name
                  </label>
                  <input
                    id="register-firstName"
                    type="text"
                    required
                    placeholder="First name"
                    className={onboardStyles.formInput}
                    autoComplete="given-name"
                    disabled={loading}
                  />
                </div>
                <div className={onboardStyles.formGroup}>
                  <label htmlFor="register-lastName" className={onboardStyles.formLabel}>
                    Last Name
                  </label>
                  <input
                    id="register-lastName"
                    type="text"
                    required
                    placeholder="Last name"
                    className={onboardStyles.formInput}
                    autoComplete="family-name"
                    disabled={loading}
                  />
                </div>
              </div>

              <div className={onboardStyles.formGroup}>
                <label htmlFor="register-password" className={onboardStyles.formLabel}>
                  Password
                </label>
                <input
                  id="register-password"
                  type="password"
                  required
                  placeholder="Choose a password"
                  minLength={8}
                  className={onboardStyles.formInput}
                  autoComplete="new-password"
                  disabled={loading}
                />
              </div>

              <div className={onboardStyles.formGroup}>
                <label htmlFor="register-confirm-password" className={onboardStyles.formLabel}>
                  Confirm Password
                </label>
                <input
                  id="register-confirm-password"
                  type="password"
                  required
                  placeholder="Confirm your password"
                  minLength={8}
                  className={onboardStyles.formInput}
                  autoComplete="new-password"
                  disabled={loading}
                />
              </div>

              {error && (
                <div className={onboardStyles.errorMessage}>
                  {error}
                </div>
              )}
            </form>
          </div>

          <div className={onboardStyles.linkContainer}>
            <button
              type="button"
              onClick={() => setShowRegisterForm(false)}
              className={onboardStyles.toggleLink}
              disabled={loading}
            >
              Already have an account?
            </button>
          </div>

          <div className={onboardStyles.buttonContainer}>
            <button
              type="button"
              onClick={handleRegister}
              disabled={loading}
              className={onboardStyles.primaryButton}
            >
              <span>
                {loading && <span className={onboardStyles.loadingSpinner}></span>}
                {loading ? 'Creating Account...' : 'Create Account'}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Show login/register forms if not authenticated
  if (!user) {
    return showRegisterForm ? <RegisterForm /> : <LoginForm />;
  }

  const handleSectionChange = (section: string) => {
    setCurrentScreen(section as Screen);
  };

  // Show appropriate screen based on currentScreen state
  const renderContent = () => {
    switch (currentScreen) {
      case 'applications':
        return <Applications onNavigate={handleSectionChange} />;
      case 'venue-mappings':
        return <VenueMappings onNavigate={handleSectionChange} />;
      case 'register':
        return (
          <RegisterOAuthApp 
            onBack={() => setCurrentScreen('dashboard')}
            onSuccess={() => setCurrentScreen('dashboard')}
            onLogout={logout}
          />
        );
      case 'test':
        return (
          <div className="test-flow-container">
            <SectionHeader
              title="Test OAuth Flow"
              description="Test the OAuth authorization flow that your users will experience"
            />
            <div className="section">
              <p>This feature will allow you to test the OAuth authorization flow with your registered applications.</p>
              <p><strong>Coming soon!</strong> This will include a mock authorization screen and flow testing.</p>
            </div>
          </div>
        );
      case 'docs':
        return <Documentation onNavigate={handleSectionChange} />;
      case 'venues':
        return <Venues onNavigate={handleSectionChange} />;
      case 'data-explorer':
        return <DataExplorer />;
      case 'charts':
        return <Charts />;
      case 'settings':
        return <Settings onNavigate={handleSectionChange} />;
      case 'dashboard':
      default:
        return <Dashboard onNavigate={handleSectionChange} />;
    }
  };

  return (
    <DeveloperPortalLayout 
      activeSection={currentScreen}
      onSectionChange={handleSectionChange}
    >
      {renderContent()}
    </DeveloperPortalLayout>
  );
};

export default DeveloperPortal;